import api from 'src/config/api';
import qs from 'qs';
import {
  User,
  UserListResponse,
  UserListSegmentationResponse,
  SegmentationReachResponse,
  Segmentation,
  SignUpWithInviteData,
  JoinWithInviteData,
} from 'src/types/user';
import {
  Post,
} from 'src/types/posts';
import {
  Acknowledgement,
} from 'src/types/acknowledgements';
import {
  ProfileDataMap,
} from 'src/types/instance';

export const getUserList = (
  search: string,
  page: number,
  limit = 10,
  filterSelf: boolean = false,
  notIncludingIds: number[] = [],
  orderBy = 'NAME',
  order = 'ASC',
) => (
  api.get<UserListResponse>(
    '/users/paginated',
    {
      params: {
        page,
        limit,
        search: search || undefined,
        filterSelf,
        notIncludingIds,
        orderBy: orderBy,
        order: order,
      },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'comma' }),
    },
  )
);

export const getUserListByIds = (
  ids: number[],
  page: number = 1,
  limit: number = 100,
) =>
  api.get<UserListResponse>(
    '/users/paginated',
    {
      params: {
        page,
        limit,
        ids: ids?.toString(),
        filterSelf: false,
      },
    },
  );

export const getUserListSegmentation = (
  segmentationItemIds: string = '',
  includeLoggedUser: boolean = false,
  page: number = 1,
  limit: number = 1,
) => {
  return api.get<UserListSegmentationResponse>(
    '/users/audience',
    {
      params: {
        page,
        limit,
        segmentationItemIds,
        includeLoggedUser,
      },
    },
  );
};

export const getAmountUsersReachSegmentation = async (
  segmentationItemIds: string = '',
  includeLoggedUser: boolean = false,
  instanceId: number,
  page: number = 1,
  limit: number = 1,
) => {
  if (!!segmentationItemIds) {
    const response = await api.get<UserListSegmentationResponse>(
      '/users/audience',
      {
        params: {
          page,
          limit,
          segmentationItemIds,
          includeLoggedUser,
        },
      },
    );

    return response.data.count;
  }

  const response = await api.get<SegmentationReachResponse>(`/instances/${instanceId}/user-stats`);

  return response.data.total;
};

export const getUserListToAcknowledge = (
  search,
  page,
  limit = 100,
) =>
  !!search
    ? api.get<UserListResponse>('/users/acknowledgeable-users', { params: { page, search, limit } })
    : api.get<UserListResponse>('/users/acknowledgeable-users', { params: { page, limit } });

export const getProfileById = (id) =>
  api.get<User>(`users/${id}`);

export const getUserPostList = (id, timestamp) =>
  !!timestamp
    ? api.get<Post[]>(`users/${id}/posts`, { params: { timestamp } })
    : api.get<Post[]>(`users/${id}/posts`);

export const getUserAcknowledgementList = (id, timestamp, limit = 10) =>
  !!timestamp
    ? api.get<Acknowledgement[]>(`users/${id}/received-acknowledgements`, { params: { timestamp, limit } })
    : api.get<Acknowledgement[]>(`users/${id}/received-acknowledgements`, { params: { limit } });

export const getUserBosses = (userId) =>
  api.get<UserListResponse>(`/organization-charts/${userId}/bosses`);

export const changeLanguageUser = (id, language: string, headers = undefined) =>
  api.patch(`/users/${id}`, { language }, { ...headers });

export const login = async (identifier, password, instanceId) => {
  if (identifier || password || instanceId) {
    const body = {
      employeeInternalId: identifier,
      password,
      instanceId,
    };
    const response = await api.post('/users/login', body);
    return response.data;
  }
  return null;
};

export const sendInvitation = (emails, token) => {
  api.post('/users/invite', emails, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const dynamicFieldsResponse = (instanceId) =>
  api.get<ProfileDataMap>(`/instances/${instanceId}/profile-fields`);

export const getUserSegmentation = (userId) =>
  api.get<Segmentation[]>(`/users/${userId}/segmentations`);

export const signUpWithInvite = (data: SignUpWithInviteData) =>
  api.post('/users/invite/sign-up', data);

export const joinWithInvite = (data: JoinWithInviteData) =>
  api.post('/instances/users', data);

export const patchUser = (id: number, data) => api.patch<User>(`users/${id}`, data);

export const patchUserProfileData = (id: number, data) => api.patch<User>(`users/${id}/profile-fields`, data);
